import React, { useState } from "react";
import { toast } from "sonner";
import { publicLink } from "@/lib/landerDomain";
import { Copy } from "@phosphor-icons/react";
import api from "@/lib/api";
import { FUNNEL_OPTIONS, VERTICAL_FUNNELS, picksLabel, picksOrAll } from "@/lib/funnelOptions";

const MODE_HINT = {
  live: "Tick the funnels you want leads from, then save. Changes go live right away. Each ticked funnel's link is on its row.",
  approval: "Tick the funnels you want leads from. Your account manager approves changes before they go live. Each live funnel's link is on its row.",
  admin_only: "Your account manager sets which funnels you get leads from. Each funnel's link is on its row.",
};
const RESULT_TOAST = {
  applied: "Saved \u2014 live now",
  pending_approval: "Sent for approval",
  withdrawn: "Request withdrawn",
  unchanged: "No changes to save",
};

function FunnelRow({ f, on, locked, link, onToggle, testId, profileId }) {
  const url = link ? publicLink(link.path) : "";
  const copy = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };
  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-2 border-black px-2 py-1.5 text-[11px] ${on ? "bg-zinc-900 text-white" : "bg-white"}`}
         data-testid={`buyer-funnel-row-${profileId}-${f.slug}`}>
      <label className={`flex w-44 shrink-0 items-center gap-2 ${locked ? "opacity-70" : "cursor-pointer"}`}>
        <input type="checkbox" className="accent-black h-3.5 w-3.5" checked={on} disabled={locked}
               onChange={() => onToggle(f.slug)} data-testid={`${testId}-${f.slug}`} />
        {f.title}
      </label>
      {link && (
        <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto sm:flex-1" data-testid={`buyer-profile-link-${profileId}-${f.slug}`}>
          <span className="min-w-0 flex-1 truncate font-mono text-[10px]" title={url}>{link.path}</span>
          <button type="button" onClick={copy} aria-label={`Copy the ${f.title} link`}
                  className={`border px-2 py-1 transition-colors duration-100 ${on ? "border-white hover:bg-white hover:text-black" : "border-black hover:bg-black hover:text-white"}`}
                  data-testid={`buyer-profile-link-copy-${profileId}-${f.slug}`}>
            <Copy size={10} weight="bold" />
          </button>
        </div>
      )}
      {on && !link && <span className="text-[10px] text-zinc-400" data-testid={`buyer-profile-link-pending-${profileId}-${f.slug}`}>Link appears once saved</span>}
    </div>
  );
}

// One list: tick the funnels you get leads from; each live funnel shows its link on the same row.
export function BuyerFunnelPicks({ profile, onUpdated }) {
  const [value, setValue] = useState(() => picksOrAll(profile.funnel_picks));
  const [busy, setBusy] = useState(false);
  const locked = profile.pick_mode === "admin_only";
  const links = Object.fromEntries((profile.links || []).map((l) => [l.funnel, l]));
  const testId = `buyer-funnel-picker-${profile.id}`;
  const saved = picksOrAll(profile.funnel_picks);
  const unchanged = !profile.pending_funnels && value.length === saved.length && value.every((s) => saved.includes(s));
  const toggle = (slug) => setValue((v) => (v.includes(slug) ? v.filter((s) => s !== slug) : [...v, slug]));
  const feedsFromInjury = !value.includes("injury") && value.some((v) => VERTICAL_FUNNELS.includes(v));
  const shown = FUNNEL_OPTIONS.filter((f) => !profile.visible_funnels || profile.visible_funnels.includes(f.slug));
  const shownOn = shown.filter((f) => value.includes(f.slug)).length;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/buyer/funnel-profiles/${profile.id}/funnels`, { funnels: value });
      onUpdated(data.profile);
      toast.success(RESULT_TOAST[data.result] || "Saved");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't save your funnels");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3 border-t border-zinc-200 pt-3" data-testid={`buyer-funnel-picks-${profile.id}`}>
      <div className="label-mono mb-1">Your funnels and links</div>
      <div className="text-[10.5px] text-zinc-600 mb-2" data-testid={`buyer-funnel-picks-hint-${profile.id}`}>{MODE_HINT[profile.pick_mode] || MODE_HINT.admin_only}</div>
      {profile.pending_funnels && (
        <div className="border border-amber-700 bg-amber-50 px-2 py-1 text-[11px] mb-2" data-testid={`buyer-funnel-picks-pending-${profile.id}`}>
          Waiting for approval: {picksLabel(profile.pending_funnels.picks)}
        </div>
      )}
      <div className="flex flex-col gap-1.5" data-testid={testId}>
        {shown.map((f) => (
          <FunnelRow key={f.slug} f={f} on={value.includes(f.slug)} locked={locked} link={value.includes(f.slug) ? links[f.slug] : null}
                     onToggle={toggle} testId={testId} profileId={profile.id} />
        ))}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[10px] text-zinc-600">
        <span data-testid={`${testId}-count`}>{shownOn} of {shown.length} picked</span>
        {!locked && (
          <button type="button" className="underline disabled:opacity-40" disabled={shownOn === shown.length}
                  onClick={() => setValue([...new Set([...value, ...shown.map((f) => f.slug)])])} data-testid={`${testId}-all`}>Pick all</button>
        )}
        {!value.length && <span className="text-red-700">Pick at least one funnel</span>}
      </div>
      {feedsFromInjury && (
        <div className="mt-1 text-[10px] text-zinc-600" data-testid={`${testId}-injury-note`}>
          Also gets matching {value.filter((v) => VERTICAL_FUNNELS.includes(v)).join(" / ")} cases from the Personal Injury (All-in-One) funnel.
        </div>
      )}
      {!locked && (
        <button type="button" onClick={save} disabled={busy || !value.length || unchanged}
                className="mt-2 border-2 border-black bg-black text-white px-3 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-40"
                data-testid={`buyer-funnel-picks-save-${profile.id}`}>
          {busy ? "Saving\u2026" : profile.pick_mode === "approval" ? "Request change" : "Save funnels"}
        </button>
      )}
    </div>
  );
}
